import { memo, useMemo, useState } from 'react';
import { BellOff, BellRing, Clock } from 'lucide-react';
import { Dropdown, IconButton, Modal, Input, Button, type DropdownItem } from '@cyphex/ui';
import { REMINDER_PRESETS, type CreateReminderRequest, type Message } from '@cyphex/types';

interface ReminderMenuProps {
  message: Message;
  onSet: (request: CreateReminderRequest) => void;
  onClear: () => void;
}

/** `datetime-local` wants `YYYY-MM-DDTHH:mm` in LOCAL time, not an ISO string. */
function toLocalInput(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * "Remind me about this" — the four presets plus a custom time.
 *
 * `minutes` is sent for the fixed offsets and NOTHING is sent for "Tomorrow
 * morning": that one depends on the user's stored timezone, so the server
 * resolves it. A client that computed it from the browser clock would fire at
 * the wrong hour for anyone travelling.
 */
export const ReminderMenu = memo(function ReminderMenu({
  message,
  onSet,
  onClear,
}: ReminderMenuProps) {
  const [customOpen, setCustomOpen] = useState(false);
  const [customAt, setCustomAt] = useState(() => toLocalInput(new Date(Date.now() + 3_600_000)));
  const armed = message.reminderAt !== null;

  const items = useMemo<DropdownItem[]>(() => {
    const presets: DropdownItem[] = REMINDER_PRESETS.map((preset) => ({
      id: preset.id,
      label: preset.label,
      icon: Clock,
      onSelect: () =>
        onSet(preset.minutes === null ? {} : { minutes: preset.minutes }),
    }));
    return [
      ...presets,
      { id: 'custom', label: 'Pick a time…', onSelect: () => setCustomOpen(true), separatorBefore: true },
      ...(armed
        ? [
            {
              id: 'clear',
              label: 'Clear reminder',
              icon: BellOff,
              destructive: true,
              onSelect: onClear,
              separatorBefore: true,
            } satisfies DropdownItem,
          ]
        : []),
    ];
  }, [armed, onClear, onSet]);

  return (
    <>
      <Dropdown
        items={items}
        label={armed ? 'Reminder armed' : 'Remind me about this'}
        align="end"
        trigger={
          <IconButton
            icon={BellRing}
            label={armed ? 'Change reminder' : 'Remind me about this'}
            size="sm"
            variant="ghost"
          />
        }
      />

      <Modal
        open={customOpen}
        onOpenChange={setCustomOpen}
        title="Remind me"
        description="Pick when this message should come back to you."
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setCustomOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={customAt.length === 0}
              onClick={() => {
                onSet({ remindAt: new Date(customAt).toISOString() });
                setCustomOpen(false);
              }}
            >
              Set reminder
            </Button>
          </>
        }
      >
        <Input
          label="Date and time"
          type="datetime-local"
          value={customAt}
          min={toLocalInput(new Date())}
          onChange={(event) => setCustomAt(event.target.value)}
        />
      </Modal>
    </>
  );
});
